<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import {computed, ref} from "vue";
import BasicCreationStep from "@/Components/Admin/Project/BasicCreationStep.vue";
import PurchaseCreationStep from "@/Components/Admin/Project/PurchaseCreationStep.vue";
import ProductionMilestoneStep from "@/Components/Admin/Project/ProductionMilestoneStep.vue";
import ProjectNotesStep from "@/Components/Admin/Project/ProjectNotesStep.vue";
import ProjectVendorStep from "@/Components/Admin/Project/ProjectVendorStep.vue";
import ProjectPriceStep from "@/Components/Admin/Project/ProjectPriceStep.vue";
import ProjectShippingStep from "@/Components/Admin/Project/ProjectShippingStep.vue";
import ProjectUploadsStep from "@/Components/Admin/Project/ProjectUploadsStep.vue";
import ProjectCreateSampleStep from "@/Components/Admin/Project/ProjectCreateSampleStep.vue";
import {useHelpers, useTenantButtonVisibility, useTenantTextRules} from "@/helpers";
import InputLabel from "@/Components/InputLabel.vue";
import {usePage} from "@inertiajs/inertia-vue3";
import ProjectNavigationButtons from "@/Components/Admin/Project/ProjectNavigationButtons.vue";
import ProjectElementsStep from "@/Components/Admin/Project/ProjectElementsStep.vue";

const formatDate = (timestamp, format) => {
  return dayjs.tz(timestamp * 1000).format(format);
}

const props = defineProps({
  project: Object,
  projectManagers: Array,
  companies: Array,
  colorField: Object,
  vendors: Array,
  taxes: Array,
  carriers: Array,
  pricingSkuOptions: Array,
  pricingSizeScaleOptions: Array,
  pricingQuantityOptions: Array,
  isGarmentColorPricingView: {
    type: Boolean,
    default: false,
  },
  pricingEquipmentRates: {
    type: Object,
    default: () => ({}),
  },
  pricingRentalPeriodOptions: {
    type: Array,
    default: () => [],
  },
  elementTypes: Array,
  elementPackages: Array,
  type: String,
  publicProjectUploadUrl: {
    type: String,
    default: null,
  },
});
const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();
const { isButtonVisible } = useTenantButtonVisibility();
const { resolveTextRule } = useTenantTextRules();

const inactiveClasses = ['border-transparent', 'hover:text-gray-600', 'hover:border-gray-300'];

const pageSelected = ref('basic');
const uploadsKey = computed(() => `${helpers.md5(JSON.stringify(props.project.image_media))}-${props.project.file_media.length}`);
const projectEntityLabel = computed(() =>
  resolveTextRule('project.edit.entity', 'Project', 'project.edit.header')
);
const purchaseOrderLabel = computed(() =>
  resolveTextRule('project.purchase.label', 'Purchase Order', 'project.edit.purchase_order')
);
const elementsTabLabel = computed(() =>
  resolveTextRule('project.edit.tab.elements', 'Elements', 'project.edit.tab.elements')
);

console.log(!(helpers.userHasPermission(currentUser, 'edit_closed_purchase_order')) && (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser)));
</script>

<template>

  <AppLayout title="Projects">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <span v-if="project.pid">Edit</span><span v-else>Create</span> {{ projectEntityLabel }}: <b v-text="project.projectname"></b>
        <b v-if="route().current('admin.projects.closed.show')">&nbsp;(THIS PROJECT IS CLOSED)</b>
      </h2>
    </template>

    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div class="text-center mb-2" v-if="project.pid">
          <ProjectNavigationButtons></ProjectNavigationButtons>
        </div>
        <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5" v-if="project.pid">
          <div class="sm:grid grid-cols-6 gap-6">
            <div>
              <InputLabel value="Project Name"></InputLabel>
              <span v-text="project.projectname"></span>
            </div>
            <div>
              <InputLabel :value="purchaseOrderLabel"></InputLabel>
              <span v-text="project.purchase_order?.purchaseorder"></span>
            </div>
            <div>
              <InputLabel value="Company Name"></InputLabel>
              <span v-text="project.company.client"></span>
            </div>
            <div>
              <InputLabel value="Project Manager"></InputLabel>
              <span v-text="project.manager?.full_name"></span>
            </div>
            <div>
              <InputLabel value="Target Delivery"></InputLabel>
              <span v-text="(project?.milestone?.prdtntrgtdelvry || '')"></span>
            </div>
            <div>
              <InputLabel value="Created At"></InputLabel>
              <span v-text="formatDate(project.created_date, 'MM/DD/YYYY')"></span>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-6 md:gap-6">
          <div class="mb-4 border-r border-gray-200">
            <ul class="text-sm font-medium text-right" role="tablist">
              <li class="mr-2" role="presentation"
                  v-if="(helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))"
              >
                <button :class="(pageSelected !== 'basic') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'basic'"
                >Basic
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))"
              >
                <button :class="(pageSelected !== 'purchase') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'purchase'"
                >Purchase
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser))"
              >
                <button :class="(pageSelected !== 'vendor') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'vendor'"
                >Vendor
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))"
              >
                <button :class="(pageSelected !== 'pricing') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'pricing'"
                >Pricing
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))">
                <button :class="(pageSelected !== 'milestone') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'milestone'"
                >Production Milestone
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))">
                <button :class="(pageSelected !== 'notes') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'notes'"
                >Notes
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))">
                <button :class="(pageSelected !== 'elements') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'elements'"
                >{{ elementsTabLabel }}
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))">
                <button :class="(pageSelected !== 'shipping') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'shipping'"
                >Order &amp; Shipping
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))">
                <button :class="(pageSelected !== 'uploads') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'uploads'"
                >Uploads
                </button>
              </li>
              <li class="mr-2" role="presentation"
                  v-if="project.pid
                    && isButtonVisible('project.edit.tab.create_sample')
                    && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))">
                <button :class="(pageSelected !== 'sample') ? inactiveClasses : []"
                        class="inline-block p-4 border-b-2 rounded-t-lg" @click="pageSelected = 'sample'"
                        v-if="!(type.includes('closed-'))"
                >Create Sample
                </button>
              </li>
            </ul>
          </div>
          <div class="col-span-5">
            <div class="col-span-6 sm:col-span-4">
              <form>
                <BasicCreationStep :projectManagers="projectManagers"
                                   v-if="(pageSelected === 'basic') && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser) || helpers.userIsCompanyContact(currentUser))"
                                   :companies="companies"
                                   :color-field="colorField"
                                   :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"/>
                <PurchaseCreationStep v-if="pageSelected === 'purchase'"
                                    :is-garment-color-pricing-view="isGarmentColorPricingView"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"/>
                <ProjectVendorStep v-if="pageSelected === 'vendor'" :vendors="vendors"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProjectVendorStep>
                <ProjectPriceStep v-if="pageSelected === 'pricing'" :taxes="taxes"
                                    :pricing-sku-options="pricingSkuOptions"
                                    :pricing-size-scale-options="pricingSizeScaleOptions"
                                    :pricing-quantity-options="pricingQuantityOptions"
                                    :is-garment-color-pricing-view="isGarmentColorPricingView"
                                    :pricing-equipment-rates="pricingEquipmentRates"
                                    :pricing-rental-period-options="pricingRentalPeriodOptions"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProjectPriceStep>
                <ProductionMilestoneStep v-if="pageSelected === 'milestone'"
                                    :is-garment-color-pricing-view="isGarmentColorPricingView"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProductionMilestoneStep>
                <ProjectElementsStep v-if="pageSelected === 'elements'" :element-types="elementTypes" :vendors="vendors"
                                     :element-packages="elementPackages"
                                     :isClosedProject="type.includes('closed-')"
                                     :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProjectElementsStep>
                <ProjectNotesStep v-if="pageSelected === 'notes'" :key="project.notes.length"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProjectNotesStep>
                <ProjectShippingStep v-if="pageSelected === 'shipping'" :carriers="carriers"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProjectShippingStep>
                <ProjectUploadsStep v-if="pageSelected === 'uploads'" :key="uploadsKey"
                                    :public-project-upload-url="publicProjectUploadUrl"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProjectUploadsStep>
                <ProjectCreateSampleStep v-if="pageSelected === 'sample' && isButtonVisible('project.edit.tab.create_sample')" :key="uploadsKey"
                                    :hide-save="type.includes('closed-') 
                                      ? !helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') 
                                      : (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></ProjectCreateSampleStep>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
